import React, { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  Autocomplete,
} from "@mui/material";
import moment from "moment";
import { Log, sendDataWithPost } from "../../services/services";
import { showSuccessMsg } from "../../utils/toast";
import { LoadingButton } from "@mui/lab";
import HeaderHelpPage from "../../components/HeaderHelpPage";
import { styles } from "../../utils/styles";
import { PAYMENT_HOST_LIST, PAYMENT_METHOD_LIST } from "../../utils/constants";

const sourceMapping = {
  'ingest_data': 'Ingest Data',
  'release_report': 'Release Report',
}

const modelMapping = {
  'fiuu-direct': 'Fiuu-Direct',
  'fiuu-direct-pf': 'Fiuu-Direct-PF',
}

const DataSubmissionFile = () => {
  const [dates, setDates] = useState({
    dci: new Date(),
    minesec: new Date(),
    psn04: new Date(),
    fiuu: new Date(),
    allinpay: new Date()
  });

  const [source, setSource] = useState({
    value: 'ingest_data',
    label: sourceMapping['ingest_data'],
  })
  const [model, setModel] = useState({
    value: 'fiuu-direct',
    label: modelMapping['fiuu-direct'],
  })
  const [allinpayFiles, setAllinpayFiles] = useState([])
  const [selectedAllinpayFile, setSelectedAllinpayFile] = useState(null)
  const [allinpayFilesLoading, setAllinpayFilesLoading] = useState(false)

  const [errors, setErrors] = useState({
    dci: false,
    minesec: false,
    psn04: false,
    fiuu: false,
    allinpay: false
  });
  
  const [loading, setLoading] = useState({
    dci: false,
    fiuu: false,
    minesec: false,
    psn04: false,
    allinpay: false,
  })

  useEffect(() => {
    if (!moment(dates.allinpay).isValid()) {
      setAllinpayFiles([])
      setSelectedAllinpayFile(null)
      return
    }

    setAllinpayFilesLoading(true)
    setSelectedAllinpayFile(null)
    sendDataWithPost(
      `/payout/ingestor/files_uploaded_v2/`,
      {
        acquirer: PAYMENT_HOST_LIST.ALLINPAY,
        upload_date: moment(dates.allinpay).format('YYYY-MM-DD'),
        status: null,
        sort_by: 'LastModified',
        sort_order: 'desc',
      },
      (data) => {
        setAllinpayFiles(data?.result || [])
        setAllinpayFilesLoading(false)
      },
      undefined,
      undefined,
      () => setAllinpayFilesLoading(false),
    ).catch(() => setAllinpayFilesLoading(false))
  }, [dates.allinpay])

  useEffect(() => {
    const newErrors = {};

    Object.keys(dates).forEach((field) => {
      const isValidDate = moment(dates[field]).isValid();
      newErrors[field] = !isValidDate;
    });

    setErrors(prev => ({ ...prev, ...newErrors }));
  }, [dates]);

  const onSubmit = async (paymentHost = 'dci', type) => {
    if (errors[type]) return
    const formatDate = moment(dates[type]).format("YYYY-MM-DD")
    const payload = {
      trigger_date: formatDate,
      payment_host: paymentHost
    }
    setLoading({ ...loading, [paymentHost]: true })
    const url = `/merchant/seds/trigger-manual/`
    Log.info("Click button [Re-Trigger] - Trigger SEDS", payload);
    await sendDataWithPost(url, payload, () => {
      showSuccessMsg(`Trigger completed`)
    })
    setLoading({ ...loading, [paymentHost]: false })
  }

  const onSubmitPSN04 = async () => {
    if (errors.psn04) return
    const formatDate = moment(dates.psn04).format("YYYY-MM")
    const payload = {
      trigger_month: formatDate,
      source: source.value
    }
    setLoading({ ...loading, psn04: true })
    const url = `/payout/report_psn04/trigger_manual/`
    Log.info("Click button [Re-Trigger] - Trigger PSN04", payload);
    await sendDataWithPost(url, payload, () => {
      showSuccessMsg(`Trigger completed`)
    })
    setLoading({ ...loading, psn04: false })
  }

  const onSubmitAllinpay = async () => {
    if (errors.allinpay || !selectedAllinpayFile) return

    const payload = {
      trigger_date: moment(dates.allinpay).format("YYYY-MM-DD"),
      payment_host: PAYMENT_HOST_LIST.ALLINPAY,
      filename: selectedAllinpayFile.FileName,
      filepath: selectedAllinpayFile.FullPath,
    }

    setLoading(prev => ({ ...prev, allinpay: true }))
    Log.info("Click button [Re-Trigger] - Trigger Allinpay", payload)
    try {
      await sendDataWithPost(`/merchant/seds/trigger-manual/`, payload, () => {
        showSuccessMsg(`Trigger completed`)
        setAllinpayFiles(prev => prev.filter(file => file.FullPath !== selectedAllinpayFile.FullPath))
        setSelectedAllinpayFile(null)
      })
    } finally {
      setLoading(prev => ({ ...prev, allinpay: false }))
    }
  }

  const updateDate = (field, value) => {
    setDates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Container>
      <HeaderHelpPage title="Data File Generation" />
      <Grid
        container
        rowSpacing={2}
      >
        <Grid item md={6} xs={6}>
          <Box>
            <Typography variant="h4" gutterBottom>
              DCI SEDS
            </Typography>
            <Typography variant="body1" sx={{ my: 3 }}>
              Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Pick a time"
                disableFuture
                inputFormat="YYYY-MM-DD"
                value={dates.dci}
                disabled={loading.dci}
                onChange={(newValue) => {
                  updateDate("dci", newValue);
                }}
                renderInput={(params) => {
                  const { error } = params
                  return <TextField {...params} error={error || !dates.dci} />
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ mt: 5 }}>
            <LoadingButton
              variant="contained"
              loading={loading.dci}
              disabled={errors.dci}
              onClick={() => { onSubmit(PAYMENT_HOST_LIST.DCI, PAYMENT_METHOD_LIST.DCI) }}
              sx={{ ...styles.customButton }}
            >
              Re-Trigger
            </LoadingButton>
          </Box>
        </Grid>
        
        <Grid item md={6} xs={6}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Minesec-Direct Transactions
            </Typography>
            <Typography variant="body1" sx={{ my: 3 }}>
              Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Pick a time"
                disableFuture
                inputFormat="YYYY-MM-DD"
                value={dates.minesec}
                disabled={loading.minesec}
                onChange={(newValue) => {
                  updateDate("minesec", newValue);
                }}
                renderInput={(params) => {
                  const { error } = params
                  return <TextField {...params} error={error || !dates.minesec} />
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ mt: 5 }}>
            <LoadingButton
              variant="contained"
              loading={loading.minesec}
              disabled={errors.minesec}
              onClick={() => { onSubmit('minesec-direct', 'minesec') }}
              sx={{ ...styles.customButton }}
            >
              Re-Trigger
            </LoadingButton>
          </Box>
        </Grid>
        <Grid item md={6} xs={6} sx={{ mt: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              PSN04
            </Typography>
            <Typography variant="body1" sx={{ my: 3 }}>
              Month
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Pick a time"
                disableFuture
                inputFormat="MMM YYYY"
                views={["year", "month"]}
                openTo="month"
                value={dates.psn04}
                disabled={loading.psn04}
                onChange={(newValue) => {
                  updateDate("psn04", newValue);
                }}
                renderInput={(params) => {
                  const { error } = params
                  return <TextField {...params} error={error || !dates.psn04} />
                }}
              />
            </LocalizationProvider>
            <FormControl sx={{ my: 1, width: '68%', mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>Source</Typography>
              <Autocomplete
                options={['ingest_data', 'release_report'].map((item) => ({
                  id: item,
                  label: sourceMapping[item],
                }))}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {sourceMapping[option.id]}
                  </li>
                )}
                onChange={(e, newValue) => {
                  if (newValue) {
                    setSource({
                      value: newValue.id,
                      label: newValue.label,
                    });
                  }
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source"
                  />
                )}
                sx={{ width: '66%' }}
                isOptionEqualToValue={(option) =>
                  option.id === source.value
                }
                value={source}
              />
            </FormControl>
          </Box>
          <Box sx={{ mt: 5 }}>
            <LoadingButton
              variant="contained"
              loading={loading.psn04}
              disabled={errors.psn04}
              onClick={onSubmitPSN04}
              sx={{ ...styles.customButton }}
            >
              Re-Trigger
            </LoadingButton>
          </Box>
        </Grid>
        <Grid item md={6} xs={6} sx={{ mt: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Fiuu-Direct Transactions
            </Typography>
            <Typography variant="body1" sx={{ my: 3 }}>
              Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Pick a time"
                disableFuture
                inputFormat="YYYY-MM-DD"
                value={dates.fiuu}
                disabled={loading.fiuu}
                onChange={(newValue) => {
                  updateDate("fiuu", newValue);
                }}
                renderInput={(params) => {
                  const { error } = params
                  return <TextField {...params} error={error || !dates.fiuu} />
                }}
              />
            </LocalizationProvider>
            <FormControl sx={{ my: 1, width: '68%', mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>Model</Typography>
              <Autocomplete
                options={Object.keys(modelMapping).map((item) => ({
                  id: item,
                  label: modelMapping[item],
                }))}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {modelMapping[option.id]}
                  </li>
                )}
                sx={{ width: '66%' }}
                onChange={(e, newValue) => {
                  if (newValue) {
                    setModel({
                      value: newValue.id,
                      label: newValue.label,
                    });
                  }
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Model"
                  />
                )}
                isOptionEqualToValue={(option) =>
                  option.id === model.value
                }
                value={model}
              />
            </FormControl>
          </Box>
          <Box sx={{ mt: 5 }}>
            <LoadingButton
              variant="contained"
              loading={loading.fiuu}
              disabled={errors.fiuu}
              onClick={() => { onSubmit(model.value, PAYMENT_HOST_LIST.FIUU) }}
              sx={{ ...styles.customButton }}
            >
              Re-Trigger
            </LoadingButton>
          </Box>
        </Grid>
        <Grid item md={6} xs={6} sx={{ mt: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Allinpay Transactions
            </Typography>
            <Typography variant="body1" sx={{ my: 3 }}>
              Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Pick a time"
                disableFuture
                inputFormat="YYYY-MM-DD"
                value={dates.allinpay}
                disabled={loading.allinpay}
                onChange={(newValue) => updateDate("allinpay", newValue)}
                renderInput={(params) => {
                  const { error } = params
                  return <TextField {...params} error={error || !dates.allinpay} />
                }}
              />
            </LocalizationProvider>
            <FormControl sx={{ my: 1, width: '68%', mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>Uploaded Files</Typography>
              <Autocomplete
                options={allinpayFiles}
                value={selectedAllinpayFile}
                loading={allinpayFilesLoading}
                disabled={loading.allinpay}
                getOptionLabel={(option) => option.FileName}
                isOptionEqualToValue={(option, value) => option.FullPath === value.FullPath}
                onChange={(event, newValue) => setSelectedAllinpayFile(newValue)}
                renderOption={(props, option) => {
                  const label = option.FileName
                  return (
                    <li
                      {...props}
                      key={option.FullPath}
                      title={label}
                      style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                    >
                      {label}
                    </li>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Uploaded Files"
                    inputProps={{
                      ...params.inputProps,
                      title: selectedAllinpayFile
                        ? selectedAllinpayFile.FileName
                        : '',
                    }}
                  />
                )}
                sx={{ width: '66%' }}
              />
            </FormControl>
          </Box>
          <Box sx={{ mt: 5 }}>
            <LoadingButton
              variant="contained"
              loading={loading.allinpay}
              disabled={errors.allinpay || !selectedAllinpayFile}
              onClick={onSubmitAllinpay}
              sx={{ ...styles.customButton }}
            >
              Re-Trigger
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataSubmissionFile;
